import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { LayoutDashboard, LogIn } from 'lucide-react';
import CodeBlock from '../components/CodeBlock';
import DocumentationLayout from '../components/DocumentationLayout';
import Button from '../components/ui/Button';
import { useAuthStore } from '../store/useAuthStore';

const extractToc = (markdownText) => {
  const lines = markdownText.split('\n');
  const newToc = [];
  lines.forEach((line) => {
    if (line.startsWith('## ')) {
      const text = line.replace('## ', '').trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-*|-*$/g, '');
      newToc.push({ level: 2, text, id });
    } else if (line.startsWith('### ')) {
      const text = line.replace('### ', '').trim();
      const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-*|-*$/g, '');
      newToc.push({ level: 3, text, id });
    }
  });
  return newToc;
};

const DocumentationPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [markdown, setMarkdown] = useState('');
  const [toc, setToc] = useState([]);

  useEffect(() => {
    fetch('/docs/Documentation.md')
      .then((res) => res.text())
      .then((text) => {
        setMarkdown(text);
        const extractedToc = extractToc(text);
        setToc(extractedToc);
      })
      .catch((err) => console.error('Failed to load markdown content:', err));
  }, []);

  return (
    <DocumentationLayout toc={toc}>
      <div className="sticky top-[-1.5rem] z-10 py-6 mb-4 flex justify-end bg-gray-100/80 backdrop-blur-md -mx-6 px-6 border-b border-slate-200/50">
        {user ? (
          <Button
            variant="secondary"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 group border-slate-200 hover:border-primary/30 hover:bg-primary/5 transition-all"
          >
            <LayoutDashboard size={18} className="text-slate-500 group-hover:text-primary transition-colors" />
            <span className="font-semibold">Back to Dashboard</span>
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={() => navigate('/login')}
            className="flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
          >
            <LogIn size={18} />
            <span className="font-semibold">Login to Portal</span>
          </Button>
        )}
      </div>

      <div className="prose max-w-none">
        <ReactMarkdown
          components={{
            code: CodeBlock,
            pre: ({ children }) => <>{children}</>,
            h2: ({ node, ...props }) => {
              const children = React.Children.toArray(props.children);
              const text = children.reduce((acc, child) => {
                if (typeof child === 'string') return acc + child;
                if (child.props && child.props.children) return acc + child.props.children;
                return acc;
              }, '');
              const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-*|-*$/g, '');
              return <h2 id={id} {...props}>{children}</h2>;
            },
            h3: ({ node, ...props }) => {
              const children = React.Children.toArray(props.children);
              const text = children.reduce((acc, child) => {
                if (typeof child === 'string') return acc + child;
                if (child.props && child.props.children) return acc + child.props.children;
                return acc;
              }, '');
              const id = text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-*|-*$/g, '');
              return <h3 id={id} {...props}>{children}</h3>;
            },
          }}
        >
          {markdown}
        </ReactMarkdown>
      </div>
    </DocumentationLayout>
  );
};

export default DocumentationPage;